/**
 * Chemistry checks — coordination queue and structured case intake.
 *
 * Queue of active cases → drawer with per-track state, availability
 * (original words + normalized), full action timeline, and a Simulate panel
 * that runs the live kickoff/availability parse. Outbound delivery is governed
 * by the server's live/watch runtime setting and is surfaced before mutations.
 */

import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { TopNav } from '../components/TopNav';
import { Button } from '../components/Button';
import { Icon, type IconName } from '../components/Icon';
import { KpiStrip, KpiTileLoading, type KpiItem } from '../components/KpiTile';
import { EmptyState } from '../components/EmptyState';
import { Modal } from '../components/Modal';
import { ChemistryCalendar } from '../components/ChemistryCalendar';
import { ChemistryBoard } from '../components/ChemistryBoard';
import { USE_MOCKS } from '../lib/supabase';
import { useAuth } from '../lib/auth';
import {
  advanceChemistryCaseLive,
  confirmChemistryTrack,
  createChemistryCase,
  getChemistryRuntime,
  listChemistryCases,
  pauseChemistryCase,
  recordChemistryCompletion,
  recordChemistrySelection,
  resolveChemistryEscalation,
  simulateChemistryParse,
} from '../lib/api';
import {
  CANNED_AVAILABILITY_TEXT,
  CANNED_KICKOFF_TEXT,
  COACH_REPLY_PREFILL,
} from '../mocks/chemistryChecks';
import { fmt } from '../lib/format';
import type {
  ChemistryAvailabilityParse,
  ChemistryAvailabilityWindow,
  ChemistryCase,
  ChemistryCaseCreateInput,
  ChemistryCaseState,
  ChemistryRuntime,
  ChemistrySimulateResponse,
  ChemistryTimelineEvent,
  ChemistryTrack,
  ChemistryTrackState,
} from '../types';

// ── State labels + pill palette ─────────────────────────────────────────────

const CASE_STATE_LABEL: Record<ChemistryCaseState, string> = {
  new: 'New',
  collecting: 'Collecting availability',
  scheduled: 'Scheduled',
  awaiting_completion: 'Awaiting completion',
  awaiting_selection: 'Awaiting selection',
  needs_human: 'Needs human',
  closed: 'Closed',
};

const TRACK_STATE_LABEL: Record<ChemistryTrackState, string> = {
  waiting_client: 'Waiting for client',
  waiting_coach: 'Waiting for coach',
  ready_to_schedule: 'Ready to schedule',
  scheduled: 'Scheduled',
  completion_unconfirmed: 'Completion unconfirmed',
  complete: 'Complete',
  needs_human: 'Needs human',
};

type PillTone = 'ok' | 'warn' | 'info' | 'neutral' | 'accent';

const TONE_STYLE: Record<PillTone, { background: string; color: string }> = {
  ok: { background: 'rgba(46,125,50,0.12)', color: '#2e7d32' },
  warn: { background: 'rgba(184,67,10,0.12)', color: 'var(--warn)' },
  info: { background: 'rgba(45,106,158,0.12)', color: 'var(--navy)' },
  neutral: { background: 'rgba(10,21,40,0.07)', color: 'var(--mute)' },
  accent: { background: 'rgba(94,53,177,0.12)', color: '#5e35b1' },
};

const CASE_TONE: Record<ChemistryCaseState, PillTone> = {
  new: 'neutral',
  collecting: 'info',
  scheduled: 'ok',
  awaiting_completion: 'info',
  awaiting_selection: 'accent',
  needs_human: 'warn',
  closed: 'neutral',
};

const TRACK_TONE: Record<ChemistryTrackState, PillTone> = {
  waiting_client: 'info',
  waiting_coach: 'info',
  ready_to_schedule: 'accent',
  scheduled: 'ok',
  completion_unconfirmed: 'info',
  complete: 'ok',
  needs_human: 'warn',
};

function Pill({ tone, children }: { tone: PillTone; children: React.ReactNode }): JSX.Element {
  return (
    <span className="pill" style={TONE_STYLE[tone]}>
      <span className="pdot" />
      {children}
    </span>
  );
}

// ── Queue page ───────────────────────────────────────────────────────────────

type QueueFilter = 'all' | 'needs_human' | 'in_flight' | 'awaiting_selection';

const FILTERS: Array<{ key: QueueFilter; label: string }> = [
  { key: 'all', label: 'All active' },
  { key: 'needs_human', label: 'Needs human' },
  { key: 'in_flight', label: 'In flight' },
  { key: 'awaiting_selection', label: 'Awaiting selection' },
];

function matchesFilter(c: ChemistryCase, f: QueueFilter): boolean {
  if (f === 'all') return c.state !== 'closed';
  if (f === 'needs_human') return c.state === 'needs_human';
  if (f === 'awaiting_selection') return c.state === 'awaiting_selection';
  return c.state === 'collecting' || c.state === 'scheduled' || c.state === 'awaiting_completion' || c.state === 'new';
}

function scheduledCount(c: ChemistryCase): number {
  return c.tracks.filter((t) =>
    t.state === 'scheduled' || t.state === 'completion_unconfirmed' || t.state === 'complete',
  ).length;
}

export function ChemistryChecks(): JSX.Element {
  const { data, isLoading } = useQuery({ queryKey: ['chemistry-cases'], queryFn: listChemistryCases });
  const runtimeQuery = useQuery({ queryKey: ['chemistry-runtime'], queryFn: getChemistryRuntime });
  const runtime = runtimeQuery.data ?? null;
  const fixtureCases = useMemo(() => data ?? [], [data]);
  const [liveCases, setLiveCases] = useState<ChemistryCase[]>([]);
  const cases = useMemo(() => [...liveCases, ...fixtureCases], [liveCases, fixtureCases]);
  const [filter, setFilter] = useState<QueueFilter>('all');
  const [view, setView] = useState<'board' | 'queue' | 'calendar'>('board');
  const [openId, setOpenId] = useState<string | null>(null);
  const [showNewCase, setShowNewCase] = useState(false);

  const updateLiveCase = (id: string, next: ChemistryCase) => {
    setLiveCases((prev) => prev.map((c) => (c.id === id ? next : c)));
  };

  const now = Date.now();
  const kpis: KpiItem[] = useMemo(() => {
    const active = cases.filter((c) => c.state !== 'closed');
    const needsHuman = active.filter((c) => c.state === 'needs_human').length;
    const waitingClient = active.flatMap((c) => c.tracks).filter((t) => t.state === 'waiting_client').length;
    const waitingCoach = active.flatMap((c) => c.tracks).filter((t) => t.state === 'waiting_coach').length;
    const invites = active.reduce((n, c) => n + scheduledCount(c), 0);
    const overdue = active.filter((c) => new Date(c.nextActionAt).getTime() < now).length;
    return [
      { label: 'Active cases', value: active.length, sub: '20–30 at steady state' },
      { label: 'Needs human', value: needsHuman, sub: 'escalated to the owning PM' },
      { label: 'Waiting on replies', value: waitingClient + waitingCoach, sub: `${waitingClient} client · ${waitingCoach} coach` },
      { label: 'Invites in place', value: invites, sub: overdue > 0 ? `${overdue} next action${overdue === 1 ? '' : 's'} due now` : 'no overdue actions' },
    ];
  }, [cases, now]);

  const filtered = cases.filter((c) => matchesFilter(c, filter));
  const openCase = cases.find((c) => c.id === openId) ?? null;

  return (
    <div className="ari-app">
      <TopNav />
      <div className="page">
        <div className="page-hd">
          <div>
            <div className="eb">Chemistry checks</div>
            <h1 className="d-title">Coordination queue</h1>
            <div className="d-meta" style={{ marginTop: 8, fontSize: 13, maxWidth: 720 }}>
              Every active chemistry-check case with its owner, current blocker, and next action.
              The assistant collects availability, matches compatible windows, and keeps every action on the case timeline.
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span
              className="pill"
              style={{
                ...TONE_STYLE[runtime?.sendEnabled ? 'warn' : runtime ? 'accent' : 'neutral'],
                fontSize: 12,
              }}
            >
              <span className="pdot" />
              {runtime?.sendEnabled
                ? 'Live email enabled'
                : runtime
                  ? 'Watch mode — no external sends'
                  : runtimeQuery.isError
                    ? 'Email mode unavailable'
                    : 'Checking email mode…'}
            </span>
            <Button variant="primary" icon="plus" onClick={() => setShowNewCase(true)}>
              New chemistry check
            </Button>
          </div>
        </div>

        {isLoading ? (
          <div className="kpi-strip">{[0, 1, 2, 3].map((i) => <KpiTileLoading key={i} />)}</div>
        ) : (
          <KpiStrip items={kpis} />
        )}

        <div className="toolbar" style={{ marginTop: 4 }}>
          <div style={{ display: 'flex', gap: 4, marginRight: 12 }}>
            {(['board', 'queue', 'calendar'] as const).map((v) => {
              const on = view === v;
              return (
                <button
                  key={v}
                  type="button"
                  onClick={() => setView(v)}
                  style={{
                    cursor: 'pointer',
                    fontSize: 12,
                    padding: '4px 12px',
                    borderRadius: 5,
                    border: '1px solid var(--border-2)',
                    background: on ? 'var(--ink, #1a2433)' : '#fff',
                    color: on ? '#fff' : 'var(--mute)',
                    fontWeight: on ? 600 : 400,
                  }}
                >
                  {v === 'board' ? 'Board' : v === 'queue' ? 'Queue' : 'Calendar'}
                </button>
              );
            })}
          </div>
          <div className="fchips" style={{ display: 'flex', gap: 6 }}>
            {FILTERS.map((f) => {
              const on = filter === f.key;
              return (
                <button
                  key={f.key}
                  type="button"
                  className="fchip"
                  onClick={() => setFilter(f.key)}
                  style={{
                    cursor: 'pointer',
                    fontSize: 12,
                    padding: '4px 10px',
                    borderRadius: 99,
                    border: on ? '1px solid transparent' : '1px solid var(--border-2)',
                    background: on ? 'var(--navy)' : '#fff',
                    color: on ? '#fff' : 'var(--mute)',
                    fontWeight: on ? 600 : 400,
                  }}
                >
                  {f.label}
                </button>
              );
            })}
          </div>
          <div className="right">
            <span className="count"><strong>{filtered.length}</strong> of {cases.length}</span>
          </div>
        </div>

        {view === 'board' ? (
          isLoading ? (
            <div className="d-meta" style={{ padding: 24 }}>Loading…</div>
          ) : (
            <ChemistryBoard cases={filtered} onOpenCase={setOpenId} />
          )
        ) : view === 'calendar' ? (
          isLoading ? (
            <div className="d-meta" style={{ padding: 24 }}>Loading…</div>
          ) : (
            <ChemistryCalendar cases={filtered} onOpenCase={setOpenId} />
          )
        ) : (
        <div style={{ flex: 1, overflow: 'auto', background: '#fff' }}>
          {isLoading ? (
            <div className="d-meta" style={{ padding: 24 }}>Loading…</div>
          ) : filtered.length === 0 ? (
            <EmptyState
              icon="search"
              title="No cases match this filter"
              body="Try a different lens — every active case is always in “All active.”"
              cta={<Button variant="secondary" onClick={() => setFilter('all')}>Show all</Button>}
            />
          ) : (
            <table className="tbl">
              <thead>
                <tr>
                  <th>Case</th>
                  <th>Client</th>
                  <th>PM</th>
                  <th>Progress</th>
                  <th>Waiting on</th>
                  <th>Target</th>
                  <th>Next action</th>
                  <th>State</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((c) => {
                  const done = scheduledCount(c);
                  const overdue = new Date(c.nextActionAt).getTime() < now;
                  return (
                    <tr key={c.id} onClick={() => setOpenId(c.id)} style={{ cursor: 'pointer' }}>
                      <td className="mono" style={{ fontWeight: 600 }}>
                        {c.tag}
                        {c.live && (
                          <span className="pill" style={{ ...TONE_STYLE.ok, fontSize: 10, marginLeft: 6, padding: '1px 7px' }}>
                            <span className="pdot" />live
                          </span>
                        )}
                      </td>
                      <td>
                        {c.clientName}
                        {c.assistantName && (
                          <span className="d-meta" style={{ fontSize: 11, display: 'block' }}>
                            via {c.assistantName} (EA)
                          </span>
                        )}
                      </td>
                      <td>{c.pmName}</td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span className="d-tnum">{done}/{c.tracks.length}</span>
                          <div style={{ width: 54, height: 4, background: 'rgba(10,21,40,0.08)' }}>
                            <div style={{ height: '100%', width: `${(done / Math.max(1, c.tracks.length)) * 100}%`, background: done === c.tracks.length ? '#2e7d32' : 'var(--navy)' }} />
                          </div>
                        </div>
                      </td>
                      <td>{c.waitingOn}</td>
                      <td className="d-tnum">{fmt.date(c.targetDate)}</td>
                      <td>
                        <span style={{ color: overdue ? 'var(--warn)' : undefined }}>
                          {c.nextAction}
                          <span className="d-meta" style={{ fontSize: 11, display: 'block', color: overdue ? 'var(--warn)' : undefined }}>
                            {overdue ? 'due now · ' : ''}{fmt.dateTime(c.nextActionAt)}
                          </span>
                        </span>
                      </td>
                      <td><Pill tone={CASE_TONE[c.state]}>{CASE_STATE_LABEL[c.state]}</Pill></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
        )}
      </div>

      {openCase && (
        <CaseDrawer
          c={openCase}
          onClose={() => setOpenId(null)}
          onAdvance={openCase.live ? (next) => updateLiveCase(openCase.id, next) : undefined}
          runtime={runtime}
        />
      )}

      {showNewCase && (
        <NewCaseModal
          onClose={() => setShowNewCase(false)}
          onCreate={(c) => {
            setLiveCases((prev) => [c, ...prev]);
            setShowNewCase(false);
            setFilter('all');
            setOpenId(c.id);
          }}
          onCreatedLive={(id) => {
            setShowNewCase(false);
            setFilter('all');
            setOpenId(id);
          }}
          liveCount={liveCases.length}
          runtime={runtime}
          runtimeError={runtimeQuery.isError}
        />
      )}
    </div>
  );
}

// ── Live-case builders (demo only — pure client-side state) ─────────────────

function nowIso(): string {
  return new Date().toISOString();
}

/** Case state, waiting-on, and next action derived from the tracks (one source of truth). */
function deriveCaseMeta(c: ChemistryCase): ChemistryCase {
  const tracks = c.tracks;
  const needsHuman = tracks.some((t) => t.state === 'needs_human') || c.state === 'needs_human';
  const allScheduled = tracks.length > 0 && tracks.every(
    (t) => t.state === 'scheduled' || t.state === 'completion_unconfirmed' || t.state === 'complete',
  );
  const waiting =
    tracks.find((t) => t.state === 'needs_human') ??
    tracks.find((t) => t.state === 'ready_to_schedule') ??
    tracks.find((t) => t.state === 'waiting_client') ??
    tracks.find((t) => t.state === 'waiting_coach');

  const state: ChemistryCaseState = needsHuman ? 'needs_human' : allScheduled ? 'scheduled' : 'collecting';
  const waitingOn = needsHuman
    ? `PM (${c.pmName})`
    : waiting?.state === 'waiting_client'
      ? `Client (${c.clientName})`
      : waiting?.state === 'waiting_coach'
        ? `Coach (${waiting.coachName})`
        : waiting?.state === 'ready_to_schedule'
          ? 'Agent — invite pending'
          : 'Nobody — all meetings confirmed';
  const nextAction = needsHuman
    ? 'PM review'
    : waiting?.nextAction ?? 'Confirm completion as meetings pass';

  return { ...c, state, waitingOn, nextAction, nextActionAt: nowIso() };
}

function buildLiveCase(
  input: ChemistryCaseCreateInput,
  pmName: string,
  pmEmail: string,
  liveCount: number,
  runtime: ChemistryRuntime,
): ChemistryCase {
  const tag = `CC-L${String(liveCount + 1).padStart(2, '0')}`;
  const clientName = input.client.name;

  const tracks: ChemistryTrack[] = input.coaches.map((coach, i) => ({
    id: `live-${tag}-t${i}`,
    coachName: coach.name,
    coachEmail: coach.email,
    coachTimezone: coach.timezone,
    state: 'waiting_client',
    waitingOn: `Client (${clientName})`,
    nextAction: 'Collect client availability',
    nextActionAt: nowIso(),
    scheduledStart: null,
    scheduledEnd: null,
    availability: [],
  }));

  const timeline: ChemistryTimelineEvent[] = [
    {
      at: nowIso(), actor: 'pm', kind: 'note', title: 'Case created from dashboard',
      detail: `${clientName} with ${tracks.length} prospective coach${tracks.length === 1 ? '' : 'es'}; target completion ${input.targetDate}.`,
    },
    {
      at: nowIso(), actor: 'agent', kind: 'email_out', shadow: !runtime.sendEnabled,
      title: `${runtime.sendEnabled ? 'Sent' : 'Would send'} availability request to ${input.assistant?.name ?? clientName}`,
      detail: `To ${input.assistant?.email ?? input.client.email}${input.assistant ? ` · CC ${input.client.email}` : ''}. Asks for 3–5 windows before the target date.`,
    },
  ];

  return deriveCaseMeta({
    id: `live-${tag}`,
    tag,
    clientName,
    clientEmail: input.client.email,
    clientTimezone: input.client.timezone,
    assistantName: input.assistant?.name ?? null,
    assistantEmail: input.assistant?.email ?? null,
    pmName,
    pmEmail,
    state: 'collecting',
    targetDate: input.targetDate,
    createdAt: nowIso(),
    waitingOn: '',
    nextAction: '',
    nextActionAt: nowIso(),
    escalationReason: null,
    tracks,
    timeline,
    live: true,
  });
}

/** Local date-only ISO n days out (mirror of the fixtures helper). */
function day(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() + days);
  d.setHours(12, 0, 0, 0);
  return d.toISOString().slice(0, 10);
}

function applyAdvance(
  c: ChemistryCase,
  from: 'client' | 'coach',
  coachName: string | null,
  fromName: string,
  r: ChemistryAvailabilityParse,
): ChemistryCase {
  const precise = r.windows.filter((w) => w.precise && w.startIso && w.endIso);
  const newWindows: ChemistryAvailabilityWindow[] = precise.map((w) => ({
    party: from,
    sourceText: w.sourceText,
    startIso: w.startIso as string,
    endIso: w.endIso as string,
    timezone: w.timezone ?? c.clientTimezone,
    status: 'valid',
  }));

  const timeline: ChemistryTimelineEvent[] = [
    ...c.timeline,
    { at: nowIso(), actor: from, kind: 'email_in', title: `${fromName} replied`, detail: r.summary },
    {
      at: nowIso(), actor: 'agent', kind: 'parse',
      title: `${precise.length} window${precise.length === 1 ? '' : 's'} normalized` +
        (r.windows.length > precise.length ? ` · ${r.windows.length - precise.length} not precise enough to book` : ''),
    },
  ];

  let tracks = c.tracks;

  if (from === 'client' && newWindows.length > 0) {
    tracks = tracks.map((t) =>
      t.state === 'waiting_client' || t.state === 'needs_human'
        ? {
            ...t,
            availability: [...t.availability, ...newWindows],
            state: 'waiting_coach',
            waitingOn: `Coach (${t.coachName})`,
            nextAction: 'Wait for the coach’s workable options',
            nextActionAt: nowIso(),
          }
        : { ...t, availability: [...t.availability, ...newWindows] },
    );
    timeline.push({
      at: nowIso(), actor: 'agent', kind: 'email_out', shadow: true,
      title: 'Would send the client’s windows to each coach separately',
      detail: 'One thread per coach — no coach sees the others.',
    });
  }

  if (from === 'coach' && coachName) {
    tracks = tracks.map((t) => {
      if (t.coachName !== coachName) return t;
      const next: ChemistryTrack = { ...t, availability: [...t.availability, ...newWindows] };
      if (r.proposedMatch) {
        next.state = 'ready_to_schedule';
        next.waitingOn = 'Agent — invite pending';
        next.nextAction = 'Create Outlook invite for the verified overlap';
        next.nextActionAt = nowIso();
      }
      return next;
    });
    if (r.proposedMatch) {
      timeline.push(
        {
          at: nowIso(), actor: 'agent', kind: 'match',
          title: `Overlap verified for ${coachName}`,
          detail: r.proposedMatch.rationale,
        },
        {
          at: nowIso(), actor: 'agent', kind: 'proposal', shadow: true,
          title: `Would create Outlook invite — ${c.clientName} × ${coachName}`,
          detail: `${fmt.dateTime(r.proposedMatch.startIso)} (${r.proposedMatch.timezone}) · 45 min · Teams · tag ${c.tag}.`,
        },
      );
    }
  }

  if (r.needsClarification && r.clarificationQuestion) {
    timeline.push({
      at: nowIso(), actor: 'agent', kind: 'email_out', shadow: true,
      title: 'Would ask a clarifying question',
      detail: `“${r.clarificationQuestion}”`,
    });
  }

  for (const d of r.draftEmails) {
    timeline.push({
      at: nowIso(), actor: 'agent', kind: 'email_out', shadow: true,
      title: `Draft — ${d.subject}`,
      detail: `To ${d.to}:\n${d.body}`,
    });
  }

  const next: ChemistryCase = {
    ...c,
    tracks,
    timeline,
    state: r.needsHuman ? 'needs_human' : c.state,
    escalationReason: r.needsHuman ? r.summary : c.escalationReason,
  };
  return deriveCaseMeta(next);
}

// ── New-case modal ───────────────────────────────────────────────────────────

const COMMON_TIMEZONES = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Toronto',
  'America/Vancouver',
  'Europe/London',
  'Europe/Paris',
  'Europe/Berlin',
  'Asia/Dubai',
  'Asia/Kolkata',
  'Asia/Singapore',
  'Asia/Tokyo',
  'Australia/Sydney',
  'Pacific/Auckland',
] as const;

const TIMEZONE_OPTIONS = (() => {
  const intl = Intl as typeof Intl & { supportedValuesOf?: (key: 'timeZone') => string[] };
  const supported = intl.supportedValuesOf?.('timeZone') ?? [...COMMON_TIMEZONES];
  return [...new Set([...COMMON_TIMEZONES, ...supported])];
})();

const TIMEZONE_SET = new Set(TIMEZONE_OPTIONS);
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
let coachRowSequence = 0;

interface CoachFormRow {
  id: string;
  name: string;
  email: string;
  timezone: string;
}

interface NewCaseFormState {
  targetDate: string;
  client: { name: string; email: string; timezone: string };
  coaches: CoachFormRow[];
  assistantEnabled: boolean;
  assistant: { name: string; email: string };
}

type NewCaseErrors = Record<string, string>;

function blankCoach(): CoachFormRow {
  coachRowSequence += 1;
  return { id: `coach-row-${coachRowSequence}`, name: '', email: '', timezone: '' };
}

function normalizeNewCaseForm(form: NewCaseFormState): NewCaseFormState {
  return {
    ...form,
    client: {
      name: form.client.name.trim(),
      email: form.client.email.trim().toLowerCase(),
      timezone: form.client.timezone.trim(),
    },
    coaches: form.coaches.map((coach) => ({
      ...coach,
      name: coach.name.trim(),
      email: coach.email.trim().toLowerCase(),
      timezone: coach.timezone.trim(),
    })),
    assistant: {
      name: form.assistant.name.trim(),
      email: form.assistant.email.trim().toLowerCase(),
    },
  };
}

function validateNewCaseForm(form: NewCaseFormState): NewCaseErrors {
  const errors: NewCaseErrors = {};
  const today = new Date().toISOString().slice(0, 10);
  if (!form.targetDate) errors.targetDate = 'Target completion date is required.';
  else if (form.targetDate < today) errors.targetDate = 'Target date cannot be in the past.';

  if (!form.client.name) errors['client.name'] = 'Client name is required.';
  if (!form.client.email) errors['client.email'] = 'Client email is required.';
  else if (!EMAIL_RE.test(form.client.email)) errors['client.email'] = 'Enter a valid email.';
  if (!form.client.timezone) errors['client.timezone'] = 'Client time zone is required.';
  else if (!TIMEZONE_SET.has(form.client.timezone)) errors['client.timezone'] = 'Choose a valid IANA time zone.';

  if (form.coaches.length === 0) errors.coaches = 'Add at least one coach.';
  if (form.coaches.length > 20) errors.coaches = 'Add no more than 20 coaches.';
  const names = new Set<string>();
  const emails = new Set<string>();
  form.coaches.forEach((coach, index) => {
    const prefix = `coaches.${index}`;
    if (!coach.name) errors[`${prefix}.name`] = 'Coach name is required.';
    if (!coach.email) errors[`${prefix}.email`] = 'Coach email is required.';
    else if (!EMAIL_RE.test(coach.email)) errors[`${prefix}.email`] = 'Enter a valid email.';
    else if (coach.email.toLowerCase() === form.client.email.toLowerCase()) {
      errors[`${prefix}.email`] = 'Coach email must differ from the client email.';
    }
    if (!coach.timezone) errors[`${prefix}.timezone`] = 'Coach time zone is required.';
    else if (!TIMEZONE_SET.has(coach.timezone)) errors[`${prefix}.timezone`] = 'Choose a valid IANA time zone.';

    const normalizedName = coach.name.toLowerCase();
    const normalizedEmail = coach.email.toLowerCase();
    if (coach.name && names.has(normalizedName)) errors[`${prefix}.name`] = 'Coach names must be unique.';
    if (coach.email && emails.has(normalizedEmail)) errors[`${prefix}.email`] = 'Coach emails must be unique.';
    names.add(normalizedName);
    emails.add(normalizedEmail);
  });

  if (form.assistantEnabled) {
    if (!form.assistant.name) errors['assistant.name'] = 'Scheduling contact name is required.';
    if (!form.assistant.email) errors['assistant.email'] = 'Scheduling contact email is required.';
    else if (!EMAIL_RE.test(form.assistant.email)) errors['assistant.email'] = 'Enter a valid email.';
  }
  return errors;
}

function newCasePayload(form: NewCaseFormState): ChemistryCaseCreateInput {
  return {
    client: { ...form.client },
    coaches: form.coaches.map(({ name, email, timezone }) => ({ name, email, timezone })),
    targetDate: form.targetDate,
    ...(form.assistantEnabled ? { assistant: { ...form.assistant } } : {}),
  };
}

function FieldError({ message }: { message?: string }): JSX.Element | null {
  return message ? <div className="input-error" style={{ marginTop: 4 }}>{message}</div> : null;
}

function TimeZoneField({
  value,
  onChange,
  error,
  placeholder,
}: {
  value: string;
  onChange(value: string): void;
  error?: string;
  placeholder: string;
}): JSX.Element {
  return (
    <>
      <input
        className="input"
        list="chemistry-timezones"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        aria-invalid={!!error}
        style={{ width: '100%' }}
      />
      <FieldError message={error} />
    </>
  );
}

function NewCaseModal({
  onClose,
  onCreate,
  onCreatedLive,
  liveCount,
  runtime,
  runtimeError,
}: {
  onClose(): void;
  onCreate(c: ChemistryCase): void;
  onCreatedLive(id: string): void;
  liveCount: number;
  runtime: ChemistryRuntime | null;
  runtimeError: boolean;
}): JSX.Element {
  const { user, displayName } = useAuth();
  const qc = useQueryClient();
  const [step, setStep] = useState<'details' | 'review'>('details');
  const [errors, setErrors] = useState<NewCaseErrors>({});
  const [form, setForm] = useState<NewCaseFormState>({
    targetDate: day(14),
    client: { name: '', email: '', timezone: '' },
    coaches: [blankCoach()],
    assistantEnabled: false,
    assistant: { name: '', email: '' },
  });

  const createLive = useMutation({
    mutationFn: (payload: ChemistryCaseCreateInput) => createChemistryCase(payload),
    onSuccess: async (id) => {
      await qc.invalidateQueries({ queryKey: ['chemistry-cases'] });
      onCreatedLive(id);
    },
  });

  const ownerEmail = user?.email ?? 'Unknown email';
  const payload = newCasePayload(form);

  const review = () => {
    const normalized = normalizeNewCaseForm(form);
    const nextErrors = validateNewCaseForm(normalized);
    setForm(normalized);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length === 0) setStep('review');
  };

  const create = () => {
    if (!runtime) return;
    if (USE_MOCKS) {
      onCreate(buildLiveCase(payload, displayName, ownerEmail, liveCount, runtime));
      return;
    }
    createLive.mutate(payload);
  };

  const updateClient = (patch: Partial<NewCaseFormState['client']>) => {
    setForm((current) => ({ ...current, client: { ...current.client, ...patch } }));
  };

  const updateCoach = (id: string, patch: Partial<CoachFormRow>) => {
    setForm((current) => ({
      ...current,
      coaches: current.coaches.map((coach) => coach.id === id ? { ...coach, ...patch } : coach),
    }));
  };

  return (
    <Modal onClose={onClose} size="lg">
      <div className="modal-hd">
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 20, alignItems: 'flex-start' }}>
          <div>
            <div className="eb">Chemistry checks</div>
            <div className="nm">New chemistry check</div>
          </div>
          <div className="d-meta" style={{ fontSize: 11.5, paddingTop: 4 }}>
            {step === 'details' ? '1 of 2 · Details' : '2 of 2 · Review'}
          </div>
        </div>
      </div>
      <div className="modal-bd" style={{ maxHeight: 'calc(100vh - 190px)', overflowY: 'auto' }}>
        {step === 'details' ? (
          <>
            <datalist id="chemistry-timezones">
              {TIMEZONE_OPTIONS.map((timezone) => <option key={timezone} value={timezone} />)}
            </datalist>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 220px', gap: 14 }}>
              <div>
                <label className="input-label">Owning project manager</label>
                <div style={{ border: '1px solid var(--border-1)', padding: '8px 10px', minHeight: 36 }}>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{displayName}</div>
                  <div className="d-meta" style={{ fontSize: 11.5 }}>{ownerEmail}</div>
                </div>
              </div>
              <div>
                <label className="input-label" htmlFor="chemistry-target-date">Target completion date</label>
                <input
                  id="chemistry-target-date"
                  className="input"
                  type="date"
                  min={new Date().toISOString().slice(0, 10)}
                  value={form.targetDate}
                  onChange={(event) => setForm((current) => ({ ...current, targetDate: event.target.value }))}
                  style={{ width: '100%' }}
                />
                <FieldError message={errors.targetDate} />
              </div>
            </div>

            <div style={{ borderTop: '1px solid var(--border-1)', paddingTop: 14 }}>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 10 }}>Client / coachee</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.25fr 1fr', gap: 10 }}>
                <div>
                  <label className="input-label">Name</label>
                  <input
                    className="input"
                    autoFocus
                    value={form.client.name}
                    onChange={(event) => updateClient({ name: event.target.value })}
                    placeholder="Dana Whitmore"
                    style={{ width: '100%' }}
                  />
                  <FieldError message={errors['client.name']} />
                </div>
                <div>
                  <label className="input-label">Email</label>
                  <input
                    className="input"
                    type="email"
                    value={form.client.email}
                    onChange={(event) => updateClient({ email: event.target.value })}
                    placeholder="dana@example.com"
                    style={{ width: '100%' }}
                  />
                  <FieldError message={errors['client.email']} />
                </div>
                <div>
                  <label className="input-label">Time zone</label>
                  <TimeZoneField
                    value={form.client.timezone}
                    onChange={(timezone) => updateClient({ timezone })}
                    error={errors['client.timezone']}
                    placeholder="Search time zones…"
                  />
                </div>
              </div>
            </div>

            <div style={{ borderTop: '1px solid var(--border-1)', paddingTop: 14 }}>
              <label style={{ display: 'flex', gap: 8, alignItems: 'center', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                <input
                  type="checkbox"
                  checked={form.assistantEnabled}
                  onChange={(event) => setForm((current) => ({ ...current, assistantEnabled: event.target.checked }))}
                />
                An assistant or EA supplies availability for the client
              </label>
              {form.assistantEnabled && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.25fr', gap: 10, marginTop: 10 }}>
                  <div>
                    <label className="input-label">Scheduling contact name</label>
                    <input
                      className="input"
                      value={form.assistant.name}
                      onChange={(event) => setForm((current) => ({
                        ...current,
                        assistant: { ...current.assistant, name: event.target.value },
                      }))}
                      placeholder="Taylor Kim"
                      style={{ width: '100%' }}
                    />
                    <FieldError message={errors['assistant.name']} />
                  </div>
                  <div>
                    <label className="input-label">Scheduling contact email</label>
                    <input
                      className="input"
                      type="email"
                      value={form.assistant.email}
                      onChange={(event) => setForm((current) => ({
                        ...current,
                        assistant: { ...current.assistant, email: event.target.value },
                      }))}
                      placeholder="taylor@example.com"
                      style={{ width: '100%' }}
                    />
                    <FieldError message={errors['assistant.email']} />
                  </div>
                </div>
              )}
            </div>

            <div style={{ borderTop: '1px solid var(--border-1)', paddingTop: 14 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', marginBottom: 10 }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>Prospective coaches</div>
                  <div className="d-meta" style={{ fontSize: 11.5, marginTop: 2 }}>
                    Each coach gets a separate scheduling track and never sees the other coaches.
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="secondary"
                  icon="plus"
                  disabled={form.coaches.length >= 20}
                  onClick={() => setForm((current) => ({ ...current, coaches: [...current.coaches, blankCoach()] }))}
                >
                  Add coach
                </Button>
              </div>
              <FieldError message={errors.coaches} />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {form.coaches.map((coach, index) => (
                  <div key={coach.id} style={{ border: '1px solid var(--border-1)', padding: 10 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                      <span className="d-meta" style={{ fontSize: 11.5, fontWeight: 600 }}>Coach {index + 1}</span>
                      {form.coaches.length > 1 && (
                        <button
                          type="button"
                          className="btn-icon"
                          title={`Remove coach ${index + 1}`}
                          aria-label={`Remove coach ${index + 1}`}
                          onClick={() => setForm((current) => ({
                            ...current,
                            coaches: current.coaches.filter((row) => row.id !== coach.id),
                          }))}
                        >
                          <Icon name="trash" size={14} />
                        </button>
                      )}
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.25fr 1fr', gap: 10 }}>
                      <div>
                        <label className="input-label">Name</label>
                        <input
                          className="input"
                          value={coach.name}
                          onChange={(event) => updateCoach(coach.id, { name: event.target.value })}
                          placeholder="Alex Rivera"
                          style={{ width: '100%' }}
                        />
                        <FieldError message={errors[`coaches.${index}.name`]} />
                      </div>
                      <div>
                        <label className="input-label">Email</label>
                        <input
                          className="input"
                          type="email"
                          value={coach.email}
                          onChange={(event) => updateCoach(coach.id, { email: event.target.value })}
                          placeholder="alex@example.com"
                          style={{ width: '100%' }}
                        />
                        <FieldError message={errors[`coaches.${index}.email`]} />
                      </div>
                      <div>
                        <label className="input-label">Time zone</label>
                        <TimeZoneField
                          value={coach.timezone}
                          onChange={(timezone) => updateCoach(coach.id, { timezone })}
                          error={errors[`coaches.${index}.timezone`]}
                          placeholder="Search time zones…"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </>
        ) : (
          <>
            <div
              style={{
                border: `1px solid ${
                  runtime?.sendEnabled
                    ? 'rgba(184,67,10,0.3)'
                    : runtime
                      ? 'rgba(94,53,177,0.25)'
                      : 'var(--border-1)'
                }`,
                background: runtime?.sendEnabled
                  ? 'rgba(184,67,10,0.06)'
                  : runtime
                    ? 'rgba(94,53,177,0.06)'
                    : 'rgba(10,21,40,0.03)',
                padding: 12,
              }}
            >
              <Pill tone={runtime?.sendEnabled ? 'warn' : runtime ? 'accent' : 'neutral'}>
                {runtime?.sendEnabled
                  ? 'Live email enabled'
                  : runtime
                    ? 'Watch mode — no external sends'
                    : 'Email mode unavailable'}
              </Pill>
              <div style={{ fontSize: 12.5, lineHeight: 1.5, marginTop: 7 }}>
                {runtime?.sendEnabled
                  ? `Creating this case will immediately email ${payload.assistant?.email ?? payload.client.email} to request availability.`
                  : runtime
                    ? 'Creating this case will record the availability request as a draft on the timeline without sending email.'
                    : 'Refresh before creating the case so the email delivery mode can be confirmed.'}
              </div>
            </div>

            <dl className="kv">
              <dt>Owning PM</dt>
              <dd>{displayName} · <span className="mono" style={{ fontSize: 12 }}>{ownerEmail}</span></dd>
              <dt>Target date</dt>
              <dd className="d-tnum">{fmt.date(payload.targetDate)}</dd>
              <dt>Meeting length</dt>
              <dd>{runtime?.meetingMinutes ?? 45} minutes · Microsoft Teams</dd>
              <dt>Client</dt>
              <dd>
                {payload.client.name} · <span className="mono" style={{ fontSize: 12 }}>{payload.client.email}</span>
                <span className="d-meta" style={{ display: 'block', fontSize: 11.5 }}>{payload.client.timezone}</span>
              </dd>
              {payload.assistant && (
                <>
                  <dt>Scheduling contact</dt>
                  <dd>
                    {payload.assistant.name} · <span className="mono" style={{ fontSize: 12 }}>{payload.assistant.email}</span>
                    <span className="d-meta" style={{ display: 'block', fontSize: 11.5 }}>Receives the request; client is copied.</span>
                  </dd>
                </>
              )}
              <dt>Coaches</dt>
              <dd>
                {payload.coaches.map((coach) => (
                  <div key={coach.email} style={{ marginBottom: 5 }}>
                    {coach.name} · <span className="mono" style={{ fontSize: 12 }}>{coach.email}</span>
                    <span className="d-meta" style={{ fontSize: 11.5 }}> · {coach.timezone}</span>
                  </div>
                ))}
              </dd>
              <dt>Initial outreach</dt>
              <dd>
                To <span className="mono" style={{ fontSize: 12 }}>{payload.assistant?.email ?? payload.client.email}</span>
                {payload.assistant && (
                  <> · CC <span className="mono" style={{ fontSize: 12 }}>{payload.client.email}</span></>
                )}
                <span className="d-meta" style={{ display: 'block', fontSize: 11.5 }}>
                  Coaches are contacted separately after client availability arrives.
                </span>
              </dd>
            </dl>
          </>
        )}
      </div>
      <div className="modal-foot" style={{ display: 'flex', gap: 8 }}>
        {step === 'review' && (
          <Button variant="ghost" icon="chevronLeft" onClick={() => setStep('details')} disabled={createLive.isPending}>
            Back
          </Button>
        )}
        <div style={{ flex: 1 }} />
        <Button variant="ghost" onClick={onClose} disabled={createLive.isPending}>Cancel</Button>
        {step === 'details' ? (
          <Button variant="primary" iconRight="arrowRight" onClick={review}>Review case</Button>
        ) : (
          <Button
            variant="primary"
            icon="check"
            disabled={!runtime || createLive.isPending}
            onClick={create}
          >
            {createLive.isPending ? 'Creating…' : 'Create case & request availability'}
          </Button>
        )}
      </div>
      {(runtimeError || createLive.isError) && (
        <div className="d-meta" style={{ padding: '0 20px 12px', color: 'var(--warn)', fontSize: 12 }}>
          {runtimeError
            ? 'Email mode could not be confirmed. Refresh before creating the case.'
            : `${(createLive.error as Error).message} — review the fields and try again.`}
        </div>
      )}
    </Modal>
  );
}

// ── Case drawer ──────────────────────────────────────────────────────────────

const TRACK_STEPS: ChemistryTrackState[] = [
  'waiting_client', 'waiting_coach', 'ready_to_schedule', 'scheduled', 'completion_unconfirmed', 'complete',
];

function TrackStepper({ state }: { state: ChemistryTrackState }): JSX.Element {
  if (state === 'needs_human') {
    return <Pill tone="warn">Needs human — paused</Pill>;
  }
  const idx = TRACK_STEPS.indexOf(state);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
      {TRACK_STEPS.map((s, i) => (
        <div key={s} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <span
            title={TRACK_STATE_LABEL[s]}
            style={{
              width: 8, height: 8, borderRadius: 99,
              background: i < idx ? '#2e7d32' : i === idx ? 'var(--navy)' : 'rgba(10,21,40,0.15)',
              outline: i === idx ? '3px solid rgba(45,106,158,0.18)' : 'none',
            }}
          />
          {i < TRACK_STEPS.length - 1 && (
            <span style={{ width: 14, height: 1, background: i < idx ? '#2e7d32' : 'rgba(10,21,40,0.15)' }} />
          )}
        </div>
      ))}
      <span className="d-meta" style={{ fontSize: 12, marginLeft: 6 }}>{TRACK_STATE_LABEL[state]}</span>
    </div>
  );
}

const KIND_ICON: Record<ChemistryTimelineEvent['kind'], IconName> = {
  email_out: 'arrowRight',
  email_in: 'arrowDown',
  parse: 'search',
  match: 'check',
  event_created: 'calendar',
  event_cancelled: 'x',
  reminder: 'refresh',
  escalation: 'alert',
  prompt: 'alert',
  proposal: 'eye',
  note: 'moreH',
};

const ACTOR_LABEL: Record<ChemistryTimelineEvent['actor'], string> = {
  agent: 'Agent',
  pm: 'PM',
  client: 'Client',
  assistant: 'Assistant',
  coach: 'Coach',
  system: 'System',
};

function TimelineRow({ e }: { e: ChemistryTimelineEvent }): JSX.Element {
  const warn = e.kind === 'escalation' || e.kind === 'event_cancelled';
  return (
    <div style={{ display: 'flex', gap: 10, padding: '8px 0', borderBottom: '1px dashed var(--border-1)' }}>
      <span style={{ color: warn ? 'var(--warn)' : 'var(--navy)', paddingTop: 2 }}>
        <Icon name={KIND_ICON[e.kind]} size={13} />
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 13, fontWeight: 600 }}>{e.title}</span>
          {e.shadow && (
            <span className="pill" style={{ ...TONE_STYLE.accent, fontSize: 10, padding: '1px 7px' }}>
              <span className="pdot" />shadow — not sent
            </span>
          )}
        </div>
        {e.detail && <div className="d-meta" style={{ fontSize: 12, marginTop: 2, lineHeight: 1.45 }}>{e.detail}</div>}
      </div>
      <div style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
        <div className="d-tnum" style={{ fontSize: 11, color: 'var(--mute)' }}>{fmt.dateTime(e.at)}</div>
        <div className="d-meta" style={{ fontSize: 11 }}>{ACTOR_LABEL[e.actor]}</div>
      </div>
    </div>
  );
}

interface TrackOps {
  confirm(trackId: string): void;
  completion(trackId: string, outcome: 'complete' | 'no_show'): void;
  busy: boolean;
}

function TrackCard({ t, clientTimezone, ops }: { t: ChemistryTrack; clientTimezone: string; ops?: TrackOps }): JSX.Element {
  const meetingPassed = t.scheduledEnd ? new Date(t.scheduledEnd).getTime() < Date.now() : false;
  return (
    <div style={{ border: '1px solid var(--border-1)', borderRadius: 6, padding: '10px 12px', background: t.state === 'needs_human' ? 'rgba(184,67,10,0.04)' : '#fff' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <strong style={{ fontSize: 13 }}>{t.coachName}</strong>
        <span className="d-meta" style={{ fontSize: 12 }}>{t.coachEmail}</span>
        {t.coachTimezone && <span className="d-meta" style={{ fontSize: 11 }}>· {t.coachTimezone}</span>}
        <div style={{ flex: 1 }} />
        <Pill tone={TRACK_TONE[t.state]}>{TRACK_STATE_LABEL[t.state]}</Pill>
      </div>
      <div style={{ marginTop: 8 }}>
        <TrackStepper state={t.state} />
      </div>
      {t.scheduledStart && (
        <div className="d-meta" style={{ fontSize: 12, marginTop: 8 }}>
          <Icon name="calendar" size={11} style={{ verticalAlign: 'middle', marginRight: 4 }} />
          {fmt.dateTime(t.scheduledStart)} · 45 min · Teams · tag in description
        </div>
      )}
      {!t.scheduledStart && t.state === 'ready_to_schedule' && t.proposedStart && (
        <div style={{ fontSize: 12, marginTop: 8, color: '#5e35b1' }}>
          <Icon name="calendar" size={11} style={{ verticalAlign: 'middle', marginRight: 4 }} />
          Proposed: {fmt.dateTime(t.proposedStart)} — awaiting the Outlook invite
        </div>
      )}
      <div className="d-meta" style={{ fontSize: 12, marginTop: 6 }}>
        <strong style={{ color: 'var(--ink)' }}>Next:</strong> {t.nextAction} · {fmt.dateTime(t.nextActionAt)}
      </div>
      {ops && t.state === 'ready_to_schedule' && t.proposedStart && (
        <div style={{ marginTop: 8 }}>
          <Button size="sm" variant="primary" icon="check" disabled={ops.busy} onClick={() => ops.confirm(t.id)}>
            Invite created — mark scheduled
          </Button>
        </div>
      )}
      {ops && (t.state === 'completion_unconfirmed' || (t.state === 'scheduled' && meetingPassed)) && (
        <div style={{ marginTop: 8, display: 'flex', gap: 6 }}>
          <Button size="sm" variant="primary" icon="check" disabled={ops.busy} onClick={() => ops.completion(t.id, 'complete')}>
            Meeting happened
          </Button>
          <Button size="sm" variant="destructive" disabled={ops.busy} onClick={() => ops.completion(t.id, 'no_show')}>
            No-show — rebook
          </Button>
        </div>
      )}
      {t.availability.length > 0 && (
        <div style={{ marginTop: 8, borderTop: '1px dashed var(--border-1)', paddingTop: 8 }}>
          {t.availability.map((w, i) => (
            <div key={i} style={{ fontSize: 12, display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap', marginBottom: 4 }}>
              <span className="d-meta" style={{ fontStyle: 'italic' }}>“{w.sourceText}”</span>
              <span style={{ color: 'var(--navy)' }}>
                → {fmt.dateTime(w.startIso)}–{new Date(w.endIso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
              </span>
              <span className="d-meta" style={{ fontSize: 11 }}>{w.timezone}{w.party === 'client' ? ` (client · ${clientTimezone})` : ''}</span>
              {w.status !== 'valid' && <span className="d-meta" style={{ fontSize: 11 }}>· {w.status}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function CaseDrawer({
  c,
  onClose,
  onAdvance,
  runtime,
}: {
  c: ChemistryCase;
  onClose(): void;
  onAdvance?: (next: ChemistryCase) => void;
  runtime: ChemistryRuntime | null;
}): JSX.Element {
  const qc = useQueryClient();
  const timeline = [...c.timeline].sort((a, b) => new Date(a.at).getTime() - new Date(b.at).getTime());
  const [selection, setSelection] = useState('');
  const refresh = () => qc.invalidateQueries({ queryKey: ['chemistry-cases'] });

  // PM operations — persisted cases in live mode only (fixtures stay static).
  const pmOps = useMutation({
    mutationFn: async (op: () => Promise<void>) => op(),
    onSuccess: refresh,
  });
  const live = !USE_MOCKS && !!c.live;
  const ops: TrackOps | undefined = live
    ? {
        confirm: (trackId) => pmOps.mutate(() => confirmChemistryTrack(trackId)),
        completion: (trackId, outcome) => pmOps.mutate(() => recordChemistryCompletion(trackId, outcome)),
        busy: pmOps.isPending,
      }
    : undefined;
  return (
    <div className="drawer-overlay" onClick={onClose}>
      <div className="drawer" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 640 }}>
        <div className="drawer-hd">
          <div>
            <div className="eb">Chemistry check · {c.tag}</div>
            <div className="nm">{c.clientName}</div>
            <div className="d-meta" style={{ marginTop: 6, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              <Pill tone={CASE_TONE[c.state]}>{CASE_STATE_LABEL[c.state]}</Pill>
              <span>Target <span className="d-tnum">{fmt.date(c.targetDate)}</span></span>
              <span>·</span>
              <span>PM {c.pmName}</span>
            </div>
          </div>
          <div className="collapse-btn" onClick={onClose} title="Close (Esc)">
            <Icon name="x" size={16} />
          </div>
        </div>

        <div className="drawer-bd">
          {c.escalationReason && (
            <div className="drawer-section">
              <div className="banner" style={{ background: 'rgba(184,67,10,0.08)', border: '1px solid rgba(184,67,10,0.25)', borderRadius: 6, padding: '10px 12px', display: 'flex', gap: 10 }}>
                <span style={{ color: 'var(--warn)', paddingTop: 1 }}><Icon name="alert" size={15} /></span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--warn)' }}>Escalated — waiting on {c.pmName}</div>
                  <div style={{ fontSize: 12.5, marginTop: 3, lineHeight: 1.5 }}>{c.escalationReason}</div>
                  {live && (
                    <div style={{ marginTop: 8 }}>
                      <Button size="sm" variant="secondary" icon="check" disabled={pmOps.isPending}
                        onClick={() => pmOps.mutate(() => resolveChemistryEscalation(c.id))}>
                        Resolve &amp; resume
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="drawer-section">
            <div className="drawer-section-h">Participants</div>
            <dl className="kv">
              <dt>Client / coachee</dt>
              <dd>{c.clientName} · <span className="mono" style={{ fontSize: 12 }}>{c.clientEmail}</span> · {c.clientTimezone}</dd>
              {c.assistantName && (
                <>
                  <dt>Scheduling contact</dt>
                  <dd>{c.assistantName} (EA) · <span className="mono" style={{ fontSize: 12 }}>{c.assistantEmail}</span>
                    <span className="d-meta" style={{ display: 'block', fontSize: 11 }}>Supplies availability; {c.clientName} remains the attendee on every invite.</span>
                  </dd>
                </>
              )}
              <dt>Owning PM</dt>
              <dd>{c.pmName} · <span className="mono" style={{ fontSize: 12 }}>{c.pmEmail}</span></dd>
              <dt>Waiting on</dt>
              <dd>{c.waitingOn}</dd>
              <dt>Next action</dt>
              <dd>{c.nextAction} <span className="d-tnum" style={{ color: 'var(--mute)' }}>· {fmt.dateTime(c.nextActionAt)}</span></dd>
            </dl>
          </div>

          <div className="drawer-section">
            <div className="drawer-section-h">Meeting tracks ({scheduledCount(c)}/{c.tracks.length} scheduled)</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {c.tracks.map((t) => <TrackCard key={t.id} t={t} clientTimezone={c.clientTimezone} ops={ops} />)}
            </div>
            <div className="d-meta" style={{ fontSize: 11, marginTop: 8 }}>
              Coach threads are separate — no coach ever sees another coach’s name, address, or availability.
            </div>
          </div>

          {live && c.state === 'awaiting_selection' && (
            <div className="drawer-section">
              <div className="drawer-section-h">Record the client’s selection</div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <select className="select" value={selection} onChange={(e) => setSelection(e.target.value)} style={{ minWidth: 200 }}>
                  <option value="">Choose the selected coach…</option>
                  {c.tracks.map((t) => <option key={t.id} value={t.coachName}>{t.coachName}</option>)}
                  <option value="__none__">No selection / decision pending</option>
                </select>
                <Button
                  size="sm"
                  variant="primary"
                  icon="check"
                  disabled={!selection || selection === '__none__' || pmOps.isPending}
                  onClick={() => pmOps.mutate(() => recordChemistrySelection(c.id, selection))}
                >
                  Record &amp; close case
                </Button>
              </div>
              <div className="d-meta" style={{ fontSize: 11.5, marginTop: 6 }}>
                Recording the selection closes the case and cancels any pending prompts.
              </div>
            </div>
          )}

          {c.live && onAdvance
            ? <AdvancePanel c={c} onAdvance={onAdvance} runtime={runtime} />
            : <SimulatePanel c={c} />}

          <div className="drawer-section">
            <div className="drawer-section-h">Case timeline</div>
            <div>
              {timeline.map((e, i) => <TimelineRow key={i} e={e} />)}
            </div>
          </div>
        </div>

        <div className="drawer-foot">
          <span className="d-meta" style={{ fontSize: 12 }}>
            Every state change and external action lands here — the audit trail is the case record.
          </span>
          <div style={{ flex: 1 }} />
          {live && c.state !== 'closed' && (
            <Button
              variant="ghost"
              disabled={pmOps.isPending}
              onClick={() => pmOps.mutate(() => pauseChemistryCase(c.id, c.waitingOn !== 'Paused'))}
            >
              {c.waitingOn === 'Paused' ? 'Resume case' : 'Pause case'}
            </Button>
          )}
          <Button variant="ghost" onClick={onClose}>Close</Button>
        </div>
      </div>
    </div>
  );
}

// ── Advance panel (live cases: play the client/coaches, case reacts) ────────

function AdvancePanel({
  c,
  onAdvance,
  runtime,
}: {
  c: ChemistryCase;
  onAdvance(next: ChemistryCase): void;
  runtime: ChemistryRuntime | null;
}): JSX.Element {
  const qc = useQueryClient();
  const senders: Array<{ key: string; from: 'client' | 'coach'; label: string; name: string; coachName: string | null }> = [
    { key: 'client', from: 'client', label: `Client — ${c.clientName}`, name: c.clientName, coachName: null },
    ...c.tracks.map((t) => ({
      key: `coach-${t.coachName}`,
      from: 'coach' as const,
      label: `Coach — ${t.coachName}`,
      name: t.coachName,
      coachName: t.coachName,
    })),
  ];
  const [senderKey, setSenderKey] = useState(senders[0]?.key ?? 'client');
  const sender = senders.find((s) => s.key === senderKey) ?? senders[0];
  const [text, setText] = useState(CANNED_AVAILABILITY_TEXT);
  const [applied, setApplied] = useState(false);
  const [result, setResult] = useState<ChemistrySimulateResponse | null>(null);

  // Live mode: one call — the server parses the reply, records it on the case,
  // moves tracks, and (watch mode) drafts the next emails. The refreshed
  // timeline shows every step.
  const applyLive = useMutation({
    mutationFn: () =>
      advanceChemistryCaseLive(c.id, { from: sender.from, coachName: sender.coachName, text }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['chemistry-cases'] });
      setApplied(true);
    },
  });

  const run = useMutation({
    mutationFn: () => {
      const knownWindows = c.tracks
        .flatMap((t) => t.availability)
        .filter((w) => w.status === 'valid')
        .map((w) => ({ party: w.party, startIso: w.startIso, endIso: w.endIso, timezone: w.timezone }));
      return simulateChemistryParse({
        mode: 'availability',
        text,
        context: {
          caseTag: c.tag,
          from: sender.from,
          fromName: sender.name,
          clientName: c.clientName,
          clientEmail: c.clientEmail,
          clientTimezone: c.clientTimezone,
          targetDate: c.targetDate,
          coachName: sender.coachName,
          knownWindows,
        },
      });
    },
    onSuccess: (r) => { setResult(r); setApplied(false); },
  });

  const pickSender = (key: string) => {
    setSenderKey(key);
    const s = senders.find((x) => x.key === key);
    setText(s?.from === 'coach' ? COACH_REPLY_PREFILL : CANNED_AVAILABILITY_TEXT);
    setResult(null);
    setApplied(false);
  };

  const apply = () => {
    if (!result?.availability) return;
    onAdvance(applyAdvance(c, sender.from, sender.coachName, sender.name, result.availability));
    setApplied(true);
  };

  return (
    <div className="drawer-section">
      <div className="drawer-section-h">Advance this case — play the participants</div>
      <div style={{ border: '1px solid var(--border-1)', borderRadius: 6, padding: 12, background: 'rgba(46,125,50,0.03)' }}>
        <div style={{ display: 'flex', gap: 6, marginBottom: 8, flexWrap: 'wrap' }}>
          {senders.map((s) => {
            const on = senderKey === s.key;
            return (
              <button
                key={s.key}
                type="button"
                onClick={() => pickSender(s.key)}
                style={{
                  cursor: 'pointer', fontSize: 12, padding: '4px 10px', borderRadius: 99,
                  border: on ? '1px solid transparent' : '1px solid var(--border-2)',
                  background: on ? 'var(--navy)' : '#fff', color: on ? '#fff' : 'var(--mute)',
                  fontWeight: on ? 600 : 400,
                }}
              >
                {s.label}
              </button>
            );
          })}
        </div>

        <textarea
          className="input"
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={4}
          style={{ width: '100%', fontSize: 13, lineHeight: 1.5, resize: 'vertical', fontFamily: 'inherit' }}
          placeholder={`Paste ${sender.name}’s reply…`}
        />

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 8, flexWrap: 'wrap' }}>
          {USE_MOCKS ? (
            <>
              <Button variant="primary" size="sm" icon="play" onClick={() => run.mutate()} disabled={run.isPending || !text.trim()}>
                {run.isPending ? 'Parsing…' : 'Parse reply'}
              </Button>
              {result?.availability && (
                <Button variant="secondary" size="sm" icon={applied ? 'check' : 'arrowRight'} onClick={apply} disabled={applied}>
                  {applied ? 'Applied to case' : 'Apply to case (shadow)'}
                </Button>
              )}
              <span className="d-meta" style={{ fontSize: 11 }}>
                The case, tracks, and timeline update in place — nothing external happens.
              </span>
            </>
          ) : (
            <>
              <Button
                variant="primary"
                size="sm"
                icon={applied ? 'check' : 'play'}
                onClick={() => { setApplied(false); applyLive.mutate(); }}
                disabled={applyLive.isPending || !text.trim() || !runtime}
              >
                {applyLive.isPending ? 'Applying…' : applied ? 'Applied — feed another' : 'Apply reply to case'}
              </Button>
              <span className="d-meta" style={{ fontSize: 11 }}>
                Parsed and recorded server-side; the timeline below refreshes with every step.
                {runtime?.sendEnabled
                  ? ' Live email is enabled, so resulting outreach may be sent.'
                  : runtime
                    ? ' Watch mode records resulting outreach as drafts without sending.'
                    : ' Email mode is unavailable; refresh before applying a reply.'}
              </span>
              {applyLive.isError && (
                <span className="d-meta" style={{ fontSize: 11, color: 'var(--warn)' }}>
                  {(applyLive.error as Error).message}
                </span>
              )}
            </>
          )}
        </div>

        {result && (
          <div style={{ marginTop: 12, borderTop: '1px solid var(--border-1)', paddingTop: 10 }}>
            {result.degraded && (
              <div style={{ marginBottom: 8 }}>
                <Pill tone="neutral">offline — showing the canned example</Pill>
              </div>
            )}
            {result.availability && <AvailabilityResult r={result.availability} />}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Simulate panel (live parse) ──────────────────────────────────────────────

function SimulatePanel({ c }: { c: ChemistryCase }): JSX.Element {
  const [mode, setMode] = useState<'kickoff' | 'availability'>('availability');
  const [text, setText] = useState(CANNED_AVAILABILITY_TEXT);
  const [result, setResult] = useState<ChemistrySimulateResponse | null>(null);

  const run = useMutation({
    mutationFn: () => {
      if (mode === 'kickoff') return simulateChemistryParse({ mode: 'kickoff', text });
      const knownWindows = c.tracks
        .flatMap((t) => t.availability)
        .filter((w) => w.status === 'valid')
        .map((w) => ({ party: w.party, startIso: w.startIso, endIso: w.endIso, timezone: w.timezone }));
      return simulateChemistryParse({
        mode: 'availability',
        text,
        context: {
          caseTag: c.tag,
          from: 'client',
          fromName: c.clientName,
          clientName: c.clientName,
          clientEmail: c.clientEmail,
          clientTimezone: c.clientTimezone,
          targetDate: c.targetDate,
          coachName: c.tracks[0]?.coachName ?? null,
          knownWindows,
        },
      });
    },
    onSuccess: setResult,
  });

  const switchMode = (m: 'kickoff' | 'availability') => {
    setMode(m);
    setText(m === 'kickoff' ? CANNED_KICKOFF_TEXT : CANNED_AVAILABILITY_TEXT);
    setResult(null);
  };

  return (
    <div className="drawer-section">
      <div className="drawer-section-h">Simulate — watch the agent read an email</div>
      <div style={{ border: '1px solid var(--border-1)', borderRadius: 6, padding: 12, background: 'rgba(45,106,158,0.03)' }}>
        <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
          {(['availability', 'kickoff'] as const).map((m) => {
            const on = mode === m;
            return (
              <button
                key={m}
                type="button"
                onClick={() => switchMode(m)}
                style={{
                  cursor: 'pointer', fontSize: 12, padding: '4px 10px', borderRadius: 99,
                  border: on ? '1px solid transparent' : '1px solid var(--border-2)',
                  background: on ? 'var(--navy)' : '#fff', color: on ? '#fff' : 'var(--mute)',
                  fontWeight: on ? 600 : 400,
                }}
              >
                {m === 'availability' ? 'Client availability reply' : 'PM kickoff email'}
              </button>
            );
          })}
        </div>

        <textarea
          className="input"
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={4}
          style={{ width: '100%', fontSize: 13, lineHeight: 1.5, resize: 'vertical', fontFamily: 'inherit' }}
          placeholder={mode === 'kickoff' ? 'Paste a kickoff email…' : 'Paste an availability reply…'}
        />

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 8 }}>
          <Button variant="primary" size="sm" icon="play" onClick={() => run.mutate()} disabled={run.isPending || !text.trim()}>
            {run.isPending ? 'Parsing…' : 'Parse it'}
          </Button>
          <span className="d-meta" style={{ fontSize: 11 }}>
            Live Claude call — edit the text and watch the parse change. Nothing is sent.
          </span>
        </div>

        {result && (
          <div style={{ marginTop: 12, borderTop: '1px solid var(--border-1)', paddingTop: 10 }}>
            {result.degraded && (
              <div style={{ marginBottom: 8 }}>
                <Pill tone="neutral">offline — showing the canned example</Pill>
              </div>
            )}
            {result.mode === 'kickoff' && result.kickoff && <KickoffResult r={result.kickoff} />}
            {result.mode === 'availability' && result.availability && <AvailabilityResult r={result.availability} />}
            {result.model && !result.degraded && (
              <div className="d-meta" style={{ fontSize: 10.5, marginTop: 8 }}>model: {result.model}</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function KickoffResult({ r }: { r: NonNullable<ChemistrySimulateResponse['kickoff']> }): JSX.Element {
  return (
    <div>
      <div style={{ fontSize: 13, lineHeight: 1.5 }}>{r.summary}</div>
      <dl className="kv" style={{ marginTop: 10 }}>
        <dt>Client</dt><dd>{r.clientName ?? <span className="em">—</span>}{r.clientTimezone ? ` · ${r.clientTimezone}` : ''}</dd>
        {r.assistantName && <><dt>Assistant</dt><dd>{r.assistantName}{r.assistantEmail ? ` · ${r.assistantEmail}` : ''}</dd></>}
        <dt>PM</dt><dd>{r.pmName ?? <span className="em">—</span>}</dd>
        <dt>Target date</dt><dd className="d-tnum">{r.targetDate ? fmt.date(r.targetDate) : <span className="em">—</span>}</dd>
        <dt>Meeting tracks</dt>
        <dd>
          {r.coaches.length === 0 ? <span className="em">—</span> : r.coaches.map((co, i) => (
            <div key={i}>{co.name}{co.email ? <span className="mono" style={{ fontSize: 11 }}> · {co.email}</span> : null}</div>
          ))}
        </dd>
      </dl>
      {r.missingFields.length > 0 && (
        <div style={{ marginTop: 10 }}>
          <Pill tone="warn">Would open in Needs human — missing: {r.missingFields.join(', ')}</Pill>
          <div className="d-meta" style={{ fontSize: 11.5, marginTop: 4 }}>
            No external email goes out until the PM fills the gap — “never guess a consequential detail.”
          </div>
        </div>
      )}
    </div>
  );
}

function AvailabilityResult({ r }: { r: NonNullable<ChemistrySimulateResponse['availability']> }): JSX.Element {
  return (
    <div>
      <div style={{ fontSize: 13, lineHeight: 1.5 }}>{r.summary}</div>

      <div style={{ marginTop: 10 }}>
        <div className="d-meta" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 6 }}>Normalized windows</div>
        {r.windows.map((w, i) => (
          <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap', fontSize: 12.5, marginBottom: 5 }}>
            <span className="d-meta" style={{ fontStyle: 'italic' }}>“{w.sourceText}”</span>
            {w.precise && w.startIso && w.endIso ? (
              <span style={{ color: 'var(--navy)' }}>
                → {fmt.dateTime(w.startIso)}–{new Date(w.endIso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })} {w.timezone ? `(${w.timezone})` : ''}
              </span>
            ) : (
              <Pill tone="warn">not precise enough to book</Pill>
            )}
            {w.note && <span className="d-meta" style={{ fontSize: 11 }}>{w.note}</span>}
          </div>
        ))}
      </div>

      {r.needsClarification && r.clarificationQuestion && (
        <div style={{ marginTop: 10, fontSize: 12.5 }}>
          <Pill tone="info">Agent would ask</Pill>
          <span style={{ marginLeft: 8, fontStyle: 'italic' }}>“{r.clarificationQuestion}”</span>
        </div>
      )}

      {r.proposedMatch && (
        <div style={{ marginTop: 10, border: '1px solid rgba(46,125,50,0.35)', background: 'rgba(46,125,50,0.05)', borderRadius: 6, padding: '8px 10px' }}>
          <div style={{ fontSize: 12.5, fontWeight: 600, color: '#2e7d32', display: 'flex', alignItems: 'center', gap: 6 }}>
            <Icon name="calendar" size={13} /> Proposed booking · {fmt.dateTime(r.proposedMatch.startIso)} ({r.proposedMatch.timezone})
          </div>
          <div className="d-meta" style={{ fontSize: 12, marginTop: 3, lineHeight: 1.45 }}>{r.proposedMatch.rationale}</div>
        </div>
      )}

      {r.draftEmails.length > 0 && (
        <div style={{ marginTop: 10 }}>
          <div className="d-meta" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 6 }}>
            Draft emails <span style={{ textTransform: 'none' }}>(shadow — nothing sent)</span>
          </div>
          {r.draftEmails.map((d, i) => (
            <div key={i} style={{ border: '1px dashed var(--border-2)', borderRadius: 6, padding: '8px 10px', marginBottom: 6, background: '#fff' }}>
              <div style={{ fontSize: 11.5, color: 'var(--mute)' }}>To: <span className="mono">{d.to}</span></div>
              <div style={{ fontSize: 12.5, fontWeight: 600, marginTop: 2 }}>{d.subject}</div>
              <div style={{ fontSize: 12, marginTop: 4, whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>{d.body}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
